import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { mediaUrl } from "../../lib/api";
import { goBooking } from "../../lib/promo";
import CaptionedImage from "../CaptionedImage";
import { useLang, pick } from "../../lib/i18n";

export default function Experiences({ experiences, bookingUrl }) {
  const { t, lang } = useLang();

  return (
    <section id="experiences" data-testid="experiences-section" className="py-24 sm:py-32 bg-[#1E1A17] text-[#F4EFEA] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs uppercase tracking-[0.3em] font-sans font-semibold text-[#9A7B66] mb-5"
        >
          {t("exp.eyebrow")}
        </motion.p>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif font-normal tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl max-w-xl"
          >
            {t("exp.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-sm text-sm text-[#A0988E] font-sans leading-relaxed"
          >
            {t("exp.intro")}
          </motion.p>
        </div>
      </div>

      <div className="nl-no-scrollbar overflow-x-auto pl-5 sm:pl-8 lg:pl-[max(2rem,calc((100vw-80rem)/2+2rem))]">
        <div className="flex gap-6 w-max pr-8">
          {(experiences || []).map((exp, i) => {
            const target = exp.booking_url || bookingUrl;
            return (
            <motion.article
              key={exp.id || i}
              data-testid={`experience-card-${i}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group relative w-[300px] sm:w-[400px] shrink-0"
            >
              <div className="rounded-md overflow-hidden h-[380px] sm:h-[480px]">
                <CaptionedImage
                  testId={`experience-image-${i}`}
                  src={mediaUrl(exp.image_url)}
                  alt={exp.image_alt || pick(exp, "title", lang)}
                  title={exp.image_title || pick(exp, "title", lang)}
                  description={exp.image_description || pick(exp, "description", lang)}
                  caption={exp.image_caption || ""}
                  focalPoint={exp.image_focal_point || "50% 0%"}
                  className="absolute inset-0 w-full h-full"
                  imageClassName="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 rounded-md bg-gradient-to-t from-[#1E1A17]/90 via-[#1E1A17]/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="font-mono text-xs text-[#9A7B66] tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-2 font-serif font-medium text-2xl text-white leading-snug">{pick(exp, "title", lang)}</h3>
                <p className="mt-2 text-sm text-white/70 font-sans leading-relaxed max-w-[90%] opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  {pick(exp, "description", lang)}
                </p>
                {target && (
                  <button
                    type="button"
                    data-testid={`experience-booking-link-${i}`}
                    onClick={() => goBooking(target)}
                    className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#9A7B66]/90 text-white text-[11px] font-sans font-semibold uppercase tracking-[0.14em] hover:bg-[#826552] transition-colors cursor-pointer"
                  >
                    {t("exp.book")} <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
